import React, { useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_API_URL;

/**
 * Props:
 * - value: { mode: "all" | "custom", ids: number[] }
 * - onChange: (next) => void
 * - seriesId?: string                      // if provided, we fetch current assignment
 * - autoHydrateFromSeries?: boolean = true // set to false to disable auto-hydration
 */
export default function PatientsSelector({
  value,
  onChange,
  seriesId,
  autoHydrateFromSeries = true,
}) {
  const [patients, setPatients] = useState([]);
  const [status, setStatus] = useState("neutral"); // neutral | loading | error
  const [assignStatus, setAssignStatus] = useState("neutral"); // for series assignment
  const [query, setQuery] = useState("");

  // Load full patient list (for rendering checkboxes/search)
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setStatus("loading");
        const res = await fetch(`${API}/patients`, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!abort) {
          setPatients(Array.isArray(data) ? data : []);
          setStatus("neutral");
        }
      } catch {
        if (!abort) setStatus("error");
      }
    })();
    return () => { abort = true; };
  }, []);

  // Load current assignment for this series (if provided)
  useEffect(() => {
    let abort = false;
    if (!seriesId || !autoHydrateFromSeries) return;

    (async () => {
      try {
        setAssignStatus("loading");
        const res = await fetch(
          `${API}/reminder-series/${encodeURIComponent(seriesId)}/patients`,
          { credentials: "include" }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const rows = await res.json(); // expected: [{id, ...}, ...]
        if (abort) return;

        const ids = Array.isArray(rows) ? rows.map(p => p.id).filter(Boolean) : [];

        // Hydrate parent state with server truth
        onChange({ mode: "custom", ids });
        setAssignStatus("neutral");
      } catch {
        if (!abort) setAssignStatus("error");
      }
    })();

    return () => { abort = true; };
  }, [seriesId, autoHydrateFromSeries, onChange]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter(p =>
      `${p.surname} ${p.forename}`.toLowerCase().includes(q) ||
      `${p.forename} ${p.surname}`.toLowerCase().includes(q)
    );
  }, [patients, query]);

  function toggle(id) {
    if (value.mode !== "custom") return;
    const nextIds = value.ids.includes(id)
      ? value.ids.filter(x => x !== id)
      : [...value.ids, id];
    onChange({ ...value, ids: nextIds });
  }

  function setMode(mode) {
    if (mode === "custom") {
      // Keep whatever selection we already have (from server or user),
      // don't auto-fill with everyone (prevents stale assignment bugs).
      onChange({ mode: "custom", ids: value.ids || [] });
    } else {
      onChange({ mode: "all", ids: [] });
    }
  }

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium">Bewohner für diesen Termin</div>

      {(assignStatus === "loading") && (
        <div className="text-xs text-gray-500">Zugewiesene Bewohner werden geladen...</div>
      )}
      {(assignStatus === "error") && (
        <div className="text-xs text-red-600">Zugewiesene Bewohner konnten nicht geladen werden.</div>
      )}

      <div className="flex items-center gap-6">
        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            name="patient-mode"
            checked={value.mode === "all"}
            onChange={() => setMode("all")}
          />
          Alle Bewohner
        </label>
        <label className="inline-flex items-center gap-2">
          <input
            type="radio"
            name="patient-mode"
            checked={value.mode === "custom"}
            onChange={() => setMode("custom")}
          />
          Individuelle Auswahl
        </label>
      </div>

      {value.mode === "custom" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Suche nach Bewohner…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div className="max-h-56 overflow-auto border rounded p-2">
            {status === "loading" && (
              <div className="text-sm text-gray-500 px-1 py-2">Bewohner werden geladen...</div>
            )}
            {status === "error" && (
              <div className="text-sm text-red-600 px-1 py-2">Bewohner konnten nicht geladen werden.</div>
            )}
            {status === "neutral" && filtered.length === 0 && (
              <div className="text-sm text-gray-500 px-1 py-2">Keine Bewohner gefunden.</div>
            )}

            {filtered.map(p => {
              const label = `${p.surname} ${p.forename} — ${p.age}`;
              return (
                <label key={p.id} className="flex items-center gap-2 py-1 px-1">
                  <input
                    type="checkbox"
                    checked={value.ids.includes(p.id)}
                    onChange={() => toggle(p.id)}
                  />
                  <span className="text-sm">{label}</span>
                </label>
              );
            })}
          </div>

          <div className="text-xs text-gray-500">
            Ausgewählt: {value.ids.length}
          </div>
        </div>
      )}
    </div>
  );
}
